import { useEffect, useState } from 'react';
import { Skeleton } from '@accure/ui';
import { LoaderCircle } from 'lucide-react';

import { Card } from '@/components/ui/card';

const LOADING_MESSAGES = [
  'Fetching system logs...',
  'Analyzing log entries...',
  'Processing filters...',
  'Preparing data...',
  'Almost there...',
];

/**
 * Loading skeleton for system logs table.
 * - Fills available space to prevent layout shift
 * - Shows realistic table structure with proper column sizing
 * - Rotates contextual messages for longer loads
 * - Maintains exact dimensions of actual table component
 */
export function SystemLogsTableLoading() {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="flex flex-1 flex-col overflow-hidden p-4">
      <div className="flex size-full flex-col">
        {/* Table header and rows skeleton */}
        <div className="flex-1 space-y-3 overflow-hidden">
          {/* Header row with realistic column widths */}
          <div className="flex gap-3 border-b border-gray-200 pb-3">
            <Skeleton className="h-5 w-[140px]" />
            <Skeleton className="h-5 w-[100px]" />
            <Skeleton className="h-5 w-[180px]" />
            <Skeleton className="h-5 w-[120px]" />
            <Skeleton className="h-5 w-[160px]" />
            <Skeleton className="h-5 w-[100px]" />
            <Skeleton className="h-5 flex-1" />
          </div>

          {/* Data rows with staggered animation */}
          <div className="space-y-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="flex gap-3 py-1"
                style={{ animationDelay: `${i * 30}ms` }}
              >
                <Skeleton className="h-4 w-[140px]" />
                <Skeleton className="h-4 w-[100px]" />
                <Skeleton className="h-4 w-[180px]" />
                <Skeleton className="h-4 w-[120px]" />
                <Skeleton className="h-4 w-[160px]" />
                <Skeleton className="h-4 w-[100px]" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        </div>

        {/* Loading message footer */}
        <div className="mt-4 flex items-center justify-center gap-2 border-t border-gray-100 pt-4">
          <LoaderCircle className="size-4 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500 transition-opacity duration-300">
            {LOADING_MESSAGES[messageIndex]}
          </span>
        </div>
      </div>
    </Card>
  );
}
